import React, { CSSProperties, useEffect, useState } from "react";

import TimeDiff from "components/basic/timer/TimeDiff";
import useCopyDataset from "hooks/useCopyDataset";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";
import { Sparklines, SparklinesBars, SparklinesLine } from "react-sparklines";
import { T } from "services/lang/LanguageService";
import { formatTime } from "utils/time-utils";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import SparkLineChart from "../sparkline-advanced/SparkLineChart";
import { ISparkLineHandlerProps } from "./SparkLineTypes";

export type ValueLabel = {
  disconnected: boolean;
  value?: number;
  decimalPlaces: number;
  content: string;
  bgColor: string;
  textColor?: string;
}

const SparkLineHandler = (props: ISparkLineHandlerProps) => {
  const value = props.dataArray[props.dataArray.length - 1];
  const decimalPlaces = props.decimalPlaces || 0;

  const [dialog, setDialog] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [valueLabel, setValueLabel] = useState<ValueLabel>({
    disconnected: false,
    value,
    decimalPlaces,
    content: `${value?.toFixed(decimalPlaces)} ${props.unit ?? ""}`,
    bgColor: "rgba(255, 255, 255, 0.6)",
    textColor: "inherit",
  });

  useEffect(() => {
    setValueLabel(prev => ({
      ...prev,
      value,
      decimalPlaces,
      content: `${value?.toFixed(decimalPlaces)} ${props.unit ?? ""}`,
    }));
  }, [value, decimalPlaces, props.unit]); 

  const labelFontSize = props.labelFontSize || "2rem";
  const sparklineWidth = props.sparklineWidth || "300px";

  const style = {
    fontSize: labelFontSize,
    fontWeight: 800,
    whiteSpace: "nowrap",
    lineHeight: "2rem",
    position: props.overlayLabel ? "absolute" : "inherit",
    top: props.vertical === "top" ? "0" : "auto",
    bottom: props.vertical === "bottom" ? "0" : "auto",
    left: props.horizontal === "left" ? "0" : "auto",
    right: props.horizontal === "right" ? "0" : "auto",
    background: valueLabel.bgColor,
    color: valueLabel.textColor,
  } as CSSProperties;
  
  const id = `sparkline_value_${(Math.random() + 1).toString(16).substring(8)}`;

  useCopyDataset(isHovered, props.title, props.dataset);

  let el;
  if (props.dataArray && props.dataArray.length !== 0) {
    const minValue = Math.min(...props.dataArray).toFixed(decimalPlaces);
    const maxValue = Math.max(...props.dataArray).toFixed(decimalPlaces);

    el = (
      <>
        {props.advanced ? (
          <SparkLineChart
            dataArray={props.dataArray}
            advancedProps={props.advancedProps}
            lineColor={props.lineColor}
            lineWidth={props.lineWidth}
            setValueLabel={setValueLabel}
          />
        ) : (
          <div
            style={{
              lineHeight: "2rem",
              width: sparklineWidth,
              height: "40px",
              display: "inline-block",
            }}
          >
            <Sparklines data={props.dataArray} height={30} preserveAspectRatio="none">
              <SparklinesLine
                style={{
                  stroke: props.lineColor ?? "#41c3f9",
                  fill: "none",
                  strokeWidth: props.lineWidth ?? 1,
                }}
              />
              <SparklinesBars style={{ fill: props.lineColor ?? "#41c3f9", fillOpacity: ".25" }} />
            </Sparklines>
          </div>
        )}
        <div
          id={`${id}`}
          className="ea-sparkline-value"
          style={style}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {valueLabel.content}
        </div>

        <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
          {props.lastValueTime && (
            <div>
              <b>{T("word.spark-line.last-value-time")}:</b> {formatTime(props.lastValueTime)} (
              {`${T("word.age")}: `}
              <TimeDiff time={props.lastValueTime} />)
            </div>
          )}
          <div>
            <b>{`${T("word.min")} ${T("word.value").toLowerCase()}`}:</b> {minValue} {props.unit}
          </div>
          <div>
            <b>{`${T("word.max")} ${T("word.value").toLowerCase()}`}:</b> {maxValue} {props.unit}
          </div>
        </Tooltip>
      </>
    );
  } else {
    el = <h4>{T("word.no-data")}</h4>;
  }

  const identifiers = [
    props.dataset,
    props.advancedProps.baseline?.usage === "dataset" && props.advancedProps?.baseline?.dataset,
    props.advancedProps.maxLine?.usage === "dataset" && props.advancedProps?.maxLine?.dataset,
    props.advancedProps.minLine?.usage === "dataset" && props.advancedProps?.minLine?.dataset,
  ].filter(Boolean) as string[];

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-evenly",
          position: "relative",
          height: `${props.height ?? "auto"}px`,
        }}
        onDoubleClick={() => setDialog(true)}
      >
        {el}
      </div>

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => {
          setDialog(false);
        }}
      >
        <DatasetLineChart
          title=""
          identifiers={identifiers}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </>
  );
};

export default SparkLineHandler;
